import React from "react";

function renderInlineContent(content) {
  if (!Array.isArray(content)) return null;
  return content.map((item, i) => {
    if (item.type === "link") {
      return (
        <a
          key={i}
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline"
        >
          {renderInlineContent(item.content)}
        </a>
      );
    }
    const s = item.styles ?? {};
    const style = {};
    if (s.textColor) style.color = s.textColor;
    if (s.backgroundColor) style.backgroundColor = s.backgroundColor;
    let node = item.text;
    if (s.code)
      node = (
        <code className="font-mono text-[12px] bg-[#f0f0f0] px-1 rounded">
          {node}
        </code>
      );
    if (s.bold) node = <strong>{node}</strong>;
    if (s.italic) node = <em>{node}</em>;
    if (s.underline)
      node = <span style={{ textDecoration: "underline" }}>{node}</span>;
    if (s.strikethrough) node = <del>{node}</del>;
    if (Object.keys(style).length)
      return (
        <span key={i} style={style}>
          {node}
        </span>
      );
    return <React.Fragment key={i}>{node}</React.Fragment>;
  });
}

function renderTableCell(cell) {
  if (typeof cell === "string") return cell;
  return renderInlineContent(cell);
}

export default function BlockNoteRenderer({
  blocks,
  showTable = false,
  tableStyle = "card",
}) {
  if (!Array.isArray(blocks) || !blocks.length) return null;
  return (
    <div className="flex flex-col gap-[8px]">
      {blocks.map((block) => {
        if (block.type === "heading") {
          const text = renderInlineContent(block.content);
          const level = block.props?.level ?? 2;
          if (level === 3)
            return (
              <h3
                key={block.id}
                className="text-[14px] font-[500] text-[#111827] mt-2"
              >
                {text}
              </h3>
            );
          return (
            <h2
              key={block.id}
              className="text-[14px] font-[400] text-[#111827]"
            >
              {text}
            </h2>
          );
        }

        if (block.type === "table") {
          const rows = block.content?.rows ?? [];
          if (!rows.length) return null;
          const [headerRow, ...bodyRows] = rows;

          if (showTable && headerRow.cells.length === 2) {
            const half = Math.ceil(bodyRows.length / 2);
            const leftRows = bodyRows.slice(0, half);
            const rightRows = bodyRows.slice(half);
            const renderKeyValue = (row) =>
              row && (
                <div className="grid grid-cols-2 gap-x-2">
                  <div className="text-[14px] text-[#636369]">
                    {renderTableCell(row.cells[0])}
                  </div>
                  <div className="text-[14px] font-[500] text-[#111827] text-right">
                    {renderTableCell(row.cells[1])}
                  </div>
                </div>
              );
            return (
              <div key={block.id} className="flex mt-1">
                <div className="flex-1 flex flex-col gap-2">
                  {leftRows.map((row, ri) => (
                    <React.Fragment key={ri}>
                      {renderKeyValue(row)}
                    </React.Fragment>
                  ))}
                </div>
                <div className="flex-1 flex flex-col gap-2 pl-[20px] ml-[20px] border-l border-[#ECECED]">
                  {rightRows.map((row, ri) => (
                    <React.Fragment key={ri}>
                      {renderKeyValue(row)}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            );
          }

          const isPlain = tableStyle === "plain";
          const align = (ci) =>
            isPlain ? "text-left" : ci === 0 ? "text-left" : "text-right";

          return (
            <div
              key={block.id}
              className={`w-full overflow-x-auto thin-scrollbar ${
                isPlain
                  ? "mt-[8px]"
                  : "rounded-[16px] border border-[#ececed] mt-1"
              }`}
            >
              <table className="w-full text-[12px] border-collapse bg-white">
                <thead className="bg-[#f5f5f5]">
                  <tr>
                    {headerRow.cells.map((cell, ci) => (
                      <th
                        key={ci}
                        className={`${isPlain ? "px-[20px] py-[8px]" : "px-[16px] py-[11px]"} font-[500] text-[12px] leading-[18px] text-[#636369] whitespace-nowrap ${align(ci)}`}
                      >
                        {renderTableCell(cell)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bodyRows.map((row, ri) => (
                    <tr
                      key={ri}
                      className={
                        isPlain
                          ? "border-b border-[#ececed]"
                          : "border-t border-[#ececed]"
                      }
                    >
                      {row.cells.map((cell, ci) => (
                        <td
                          key={ci}
                          className={`${isPlain ? "px-[20px] py-[8px] font-[400]" : "px-[16px] py-[8px] font-[500]"} text-[14px] leading-[20px] text-[#111827] whitespace-nowrap ${align(ci)}`}
                        >
                          {renderTableCell(cell)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }

        const inline = renderInlineContent(block.content);
        if (!inline) return null;
        return (
          <p
            key={block.id}
            className="text-[14px] text-[#111827] leading-[20px]"
          >
            {inline}
          </p>
        );
      })}
    </div>
  );
}
